import React from 'react';
import { Row, Col, Form } from 'antd';
import { theme, InputComponent, SelectComponent, Text } from '@pfmam/pfmam';
import { CaretDownOutlined } from '@ant-design/icons';

// Field names rendered by this component. Wire instruction fields are PascalCase,
// beneficiary address fields are camelCase, so the two sets never collide.
export const BENEFICIARY_ADDRESS_FIELDS = [
  'buildingNumber',
  'streetName',
  'buildingName',
  'department',
  'floorNumber',
  'roomNumber',
  'city',
  'county',
  'state',
  'zipCode',
  'country',
];

export const pickBeneficiaryAddressFields = (values) => {
  const picked = {};
  BENEFICIARY_ADDRESS_FIELDS.forEach((field) => {
    const value = values[field];
    picked[field] = typeof value === 'string' ? value.trim() : value;
  });
  picked.country = 'US';
  return picked;
};

export const omitBeneficiaryAddressFields = (values) => {
  const remaining = { ...values };
  BENEFICIARY_ADDRESS_FIELDS.forEach((field) => {
    delete remaining[field];
  });
  return remaining;
};

// The API returns PascalCase keys, the form uses camelCase.
export const mapBeneficiaryAddressToForm = (address) => ({
  department: address.Department || address.department || '',
  buildingNumber: address.BuildingNumber || address.buildingNumber || '',
  streetName: address.StreetName || address.streetName || '',
  buildingName: address.BuildingName || address.buildingName || '',
  floorNumber: address.FloorNumber || address.floorNumber || '',
  roomNumber: address.RoomNumber || address.roomNumber || '',
  zipCode: address.ZipCode || address.zipCode || '',
  city: address.City || address.city || '',
  county: address.County || address.county || '',
  state: address.State || address.state || '',
  country: 'US',
});

// Strips non printable-ASCII characters and leading whitespace
const normalizeText = (value) =>
  value?.replace(/[^\x20-\x7E]/g, '').replace(/^\s+/, '');

const validateZipCode = (_, value) => {
  if (!value || !value.trim()) {
    return Promise.reject(new Error('Zip Code is required'));
  }
  if (value.length > 16) {
    return Promise.reject(new Error('Zip Code must not exceed 16 characters'));
  }
  if (!/^[0-9\-]*$/.test(value)) {
    return Promise.reject(
      new Error('Zip Code can only contain numbers and hyphens')
    );
  }
  return Promise.resolve();
};

const BeneficiaryAddressFields = ({ states, disabled }) => {
  return (
    <>
      <Text
        label="Beneficiary Address"
        fontSize={`${theme.font_size.size_3xl}`}
        color={`${theme.colors.black_secondary}`}
        lineheight="32px"
        margin="8px 0 4px"
      />
      <p
        style={{
          fontSize: '14px',
          lineHeight: '20px',
          whiteSpace: 'normal',
          color: theme.colors.black_tertiary,
          margin: '0 0 24px',
        }}
      >
        Effective Fall 2026, all wire instructions must include the
        beneficiary's (recipient of the funds) physical address. This
        requirement is driven by global wire processing standards (ISO 20022)
        and applies across the industry, not specifically to the Fund. Complete
        all required fields to avoid processing delays.
      </p>

      <Row gutter={40}>
        <Col span={12}>
          <Form.Item
            label="Building Number"
            name="buildingNumber"
            normalize={normalizeText}
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Building Number is required',
              },
            ]}
          >
            <InputComponent
              type="default"
              placeholder="Enter Building Number"
              maxLength={16}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="City"
            name="city"
            normalize={normalizeText}
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'City is required',
              },
            ]}
          >
            <InputComponent
              type="default"
              placeholder="Enter City"
              maxLength={35}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col span={12}>
          <Form.Item
            label="Street Name"
            name="streetName"
            normalize={normalizeText}
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Street Name is required',
              },
            ]}
          >
            <InputComponent
              type="default"
              placeholder="Enter Street Name"
              maxLength={70}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="State"
            name="state"
            rules={[
              {
                required: true,
                message: 'State is required',
              },
            ]}
          >
            <SelectComponent
              option={states}
              placeholder="Select State"
              suffixIcon={<CaretDownOutlined />}
              dropdownClassName="form-select-dropdown"
              bordered={true}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col span={12}>
          <Form.Item
            label="Building Name"
            name="buildingName"
            normalize={normalizeText}
          >
            <InputComponent
              type="default"
              placeholder="Enter Building Name, if applicable"
              maxLength={35}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Zip Code"
            name="zipCode"
            normalize={normalizeText}
            rules={[
              {
                required: true,
                validator: validateZipCode,
              },
            ]}
          >
            <InputComponent
              type="default"
              placeholder="Enter Zip Code (e.g., 12345 or 12345-6789)"
              maxLength={16}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col span={12}>
          <Form.Item
            label="Department"
            name="department"
            normalize={normalizeText}
          >
            <InputComponent
              type="default"
              placeholder="Enter Department, if applicable"
              maxLength={70}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="County" name="county" normalize={normalizeText}>
            <InputComponent
              type="default"
              placeholder="Enter County, if applicable"
              maxLength={35}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col span={12}>
          <Form.Item
            label="Floor Number"
            name="floorNumber"
            normalize={normalizeText}
          >
            <InputComponent
              type="default"
              placeholder="Enter Floor Number, if applicable"
              maxLength={70}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Room Number"
            name="roomNumber"
            normalize={normalizeText}
          >
            <InputComponent
              type="default"
              placeholder="Enter Room Number, if applicable"
              maxLength={70}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col span={12}>
          <Form.Item
            label="Country"
            name="country"
            initialValue="US"
            rules={[
              {
                required: true,
                message: 'Country is required',
              },
            ]}
          >
            <InputComponent type="default" placeholder="US" disabled={true} />
          </Form.Item>
        </Col>
        <Col span={12} />
      </Row>
    </>
  );
};

export default BeneficiaryAddressFields;
